import { Injectable } from '@angular/core';
import { PdfBuilderUtilitiesService } from './pdf-builder-utilities.service';
import { TranslationService } from './translation.service';

@Injectable({
  providedIn: 'root'
})
export class PdfBuilderDataShaperService {
  private defaultFallback = 'No especificado';

  constructor(private pdfUtils: PdfBuilderUtilitiesService, private translation: TranslationService) { }

  shapeAttentionData(objRes: any) {
    const shaped =  {
      metadata: this.shapeMetadata(objRes),
      affiliation: this.shapeAffiliation(objRes),
      anamnesis: {
        specialty: this.isNullOrEmpty(objRes?.specialty) ? this.defaultFallback : objRes.specialty.toUpperCase(),
        reason: this.isNullOrEmpty(objRes?.motive) ? this.defaultFallback : objRes.motive,
        currentIllness: this.isNullOrEmpty(objRes?.story) ? this.defaultFallback : objRes.story,
        personalHistory: this.isNullOrEmpty(objRes?.backgroundself) ? this.defaultFallback : objRes.backgroundself,
        paternalHistory: this.isNullOrEmpty(objRes?.backgroundFather) ? this.defaultFallback : objRes?.backgroundFather,
        maternalHistory: this.isNullOrEmpty(objRes?.backgroundMother) ? this.defaultFallback : objRes?.backgroundMother,
        allergies: this.isNullOrEmpty(objRes?.allergies) ? this.defaultFallback : objRes.allergies,
        medications: this.isNullOrEmpty(objRes?.medications) ? this.defaultFallback : objRes.medications,
        biologicalFunctions: this.shapeBiologicalFunctions(objRes),
        sicknessTime: this.isNullOrEmpty(objRes?.sicknessTime) ? this.defaultFallback : objRes.sicknessTime,
        sicknessUnit: this.isNullOrEmpty(objRes?.sicknessUnit) ? '' : objRes.sicknessUnit,
        symptomsList: this.isNullOrEmpty(objRes?.symptomsList) ? this.defaultFallback : objRes.symptomsList.trim(),
        additionalComments: this.isNullOrEmpty(objRes?.additionalComments) ? 'No especificadas' : objRes.additionalComments
      },
      clinicalExam: this.shapeClinicalExamSection(objRes),
      diagnosis: this.shapeDiagnosis(objRes),
      treatment: this.shapeTreatmentList(objRes),
      footer: this.shapeFooter(objRes),
      signature: this.shapeSignature(objRes)
    };

    return this.pdfUtils.createDebugProxy(shaped, 'shaped');
  }

  shapeTriageData(objRes: any) {
    const shaped = {
      patientData: this.shapeBasicPatientData(objRes),
      appointmentReason: {
        reason: this.isNullOrEmpty(objRes?.motive) ? this.defaultFallback : objRes.motive,
        additionalComments: this.isNullOrEmpty(objRes?.additionalComments) ? 'No especificadas' : objRes.additionalComments
      },
      biologicalFunctions: this.shapeBiologicalFunctions(objRes),
      vitalSigns: this.shapeVitalSigns(objRes, false),
      metadata: this.shapeMetadata(objRes),
      footer: this.shapeFooter(objRes),
      signature: this.shapeSignature(objRes)
    };
    return this.pdfUtils.createDebugProxy(shaped, 'shaped');
  }

  shapePrescriptionData(objRes: any) {
    const shaped = {
      metadata: this.shapeMetadata(objRes),
      patientData: this.shapeBasicPatientData(objRes),
      diagnosis: this.shapeDiagnosis(objRes),
      medications: this.shapeMedicationList(objRes),
      footer: this.shapeFooter(objRes),
      signature: this.shapeSignature(objRes)
    }
    return this.pdfUtils.createDebugProxy(shaped, 'shaped');
  }

  shapeExamData(objRes: any) {
    const shaped = {
      metadata: this.shapeMetadata(objRes),
      patientData: this.shapeBasicPatientData(objRes),
      diagnosis: this.shapeDiagnosis(objRes),
      exams: this.shapeExams(objRes),
      footer: this.shapeFooter(objRes),
      signature: this.shapeSignature(objRes)
    }
    return this.pdfUtils.createDebugProxy(shaped, 'shaped');
  }

  shapeInterconsultationData(objRes: any) {
    const shaped = {
      metadata: this.shapeMetadata(objRes),
      patientData: this.shapeBasicPatientData(objRes),
      diagnosis: this.shapeDiagnosis(objRes),
      interconsultation: {
        specialtyFrom: this.isNullOrEmpty(objRes?.specialityOrigin) ? this.defaultFallback : objRes.specialityOrigin,
        // specialtyTo: this.isNullOrEmpty(objRes?.specialityTarget) ? this.defaultFallback : objRes.specialityTarget,
        referrals: this.shapeInterconsultationList(objRes),
        reason: this.isNullOrEmpty(objRes?.reasonInter) ? this.defaultFallback : objRes.reasonInter,
        physicalExamSummary: this.isNullOrEmpty(objRes?.physicalExman) ? this.defaultFallback : objRes.physicalExman,
      },
      footer: this.shapeFooter(objRes),
      signature: this.shapeSignature(objRes)
    }
    return this.pdfUtils.createDebugProxy(shaped, 'shaped');
  }

  shapeAffiliationData(patient: any) {
    const shaped = {
      patientData: this.shapeDetailedPatientData(patient),
    }
    return this.pdfUtils.createDebugProxy(shaped, 'shaped');
  }

  shapeNotesData(objRes: any) {
    const shaped = {
      metadata: this.shapeMetadata(objRes),
      footer: this.shapeFooter(objRes),
      notes: this.shapeNotes(objRes),
      signature: this.shapeSignature(objRes)
    }
    return this.pdfUtils.createDebugProxy(shaped, 'shaped');
  }

  private isNullOrEmpty(str: any): boolean {
    return str == null || str.toString().trim() === '';
  }

  private shapeInterconsultationList(obj: any) {
    const referralList = obj.referralList?.split('###').map(entry => {
      const [specialty, name, code, reason] = entry?.split('&&');
      return {
        specialty: `${this.translation.translateSentence(specialty ?? '')?.trim()}`, // No header for first column
        name: `${!this.isNullOrEmpty(name) ? this.translation.translateSentence(name ?? '')?.trim() : ''}`,
        code: !this.isNullOrEmpty(code) ? code?.trim() : '',
        reason: `${!this.isNullOrEmpty(reason) ? this.translation.translateSentence(reason ?? '')?.trim() : ''}`
      };
    }) ?? [];
    return referralList
  }

  private shapeDiagnosis(obj: any): {
    diagnosisList: { label: string; value: string; code: string; }[];
    diagnosisObservations: string;
  } {
    const diagnosisList = this.shapeDiagnosisList(obj) ?? [];
    const diagnosisObservations = this.shapeDiagnosisObservations(obj) ?? 'No especificadas';
    return {
      diagnosisList,
      diagnosisObservations
    };
  }

  private shapeDiagnosisObservations(obj: any): string {
    return this.isNullOrEmpty(obj?.diagnosisObservations) ? 'No especificadas' : obj.diagnosisObservations;
  }

  private shapeDiagnosisList(obj: any): { 
    label: string; 
    value: string; 
    code: string; 
  }[] {
    const diagnosisList = obj.diagnosisList?.split('###').map(entry => {
      const [name, description, code] = entry.split('&&');
      return {
        label: '', // No header for first column
        value: `${this.translation.translateSentence(name).trim()} - ${this.translation.translateSentence(description).trim()}`,
        code: code?.trim() ?? '',
      };
    }) ?? [];
    return diagnosisList
  }

  private shapeTreatmentList(obj: any) {
    const treatment = obj.treatment;
    if (!treatment || typeof treatment !== 'string') return [];
  
    const entries: { label: string; value: string }[] = [];
    const treatments = treatment.split('###');
  
    for (const treatmentEntry of treatments) {
      // Check if the entry is structured (contains && separators with a code like CPT:xxxxx)
      const parts = treatmentEntry.split('&&');
      const nonEmptyParts = parts.filter(p => p.trim() !== '');
      const hasStructuredFormat = nonEmptyParts.length >= 2 && nonEmptyParts.some(p => p.includes(':'));

      if (hasStructuredFormat) {
        const label = nonEmptyParts[0];
        const codePart = nonEmptyParts.find(p => p.includes(':')) ?? '';
        const description = nonEmptyParts.filter(p => p !== label && p !== codePart).join(' ').trim();
        const [codeType, code] = codePart?.split(':') ?? ['Código', ''];
  
        entries.push({
          label: '',
          value: `${label?.trim() ?? ''}${description?.trim() ? ' - ' + description.trim() : ''}`,
        });
  
        entries.push({
          label: codeType?.trim().length > 0 ? codeType.trim() : 'Código',
          value: code?.trim().length > 0 ? code.trim() : 'N/A',
        });
      } else {
        // Fallback for unstructured treatment entry
        const fallbackText = treatmentEntry.trim();
        if (fallbackText.length > 0) {
          entries.push({
            label: '',
            value: fallbackText,
          });
  
          entries.push({
            label: 'Código',
            value: 'N/A',
          });
        }
      }
    }
  
    return entries;
  }

  private shapeExams(obj: any) {
    return {
      examList: this.shapeExamList(obj),
      examObservations: this.shapeExamObservations(obj)
    };
  }

  private shapeExamObservations(obj: any) {
    return this.isNullOrEmpty(obj?.examObservations) ? 'No especificadas' : obj.examObservations;
  }

  private shapeExamList(obj: any): { label: string; value: string }[] {
    const raw = obj.examList;
    if (!raw || typeof raw !== 'string') return [];
  
    const entries: { label: string; value: string }[] = [];
  
    const exams = raw.split('###');
    for (const exam of exams) {
      const fields = exam.split('&&').reduce((acc, part) => {
        const [key, val] = part.split('=');
        acc[key?.trim()] = val?.trim();
        return acc;
      }, {} as Record<string, string>);
  
      const label = fields.label ?? 'Examen';
      const code = fields.code ?? '';
  
      entries.push({ label: '', value: label });
      entries.push({ label: 'CPT', value: code.length > 0 ? code : 'N/A' });
    }
  
    return entries;
  }

  private shapeMedicationList(obj: any): { label: string; value: string }[] {
    const treatment = obj.treatment;
    if (!treatment || typeof treatment !== 'string') return [];
  
    const entries: { label: string; value: string }[] = [];
    const treatments = treatment.split('###');
  
    for (const item of treatments) {
      if (this.isNullOrEmpty(item.trim())) {
        continue;
      }
      const fieldPairs = item.split('&&');
      const fields: Record<string, string> = {};
  
      for (const pair of fieldPairs) {
        const [keyRaw, ...rest] = pair.split('=');
        const key = keyRaw?.trim();
        const value = rest.join('=').trim();
        if (key) {
          fields[key] = value;
        }
      }
  
      // Compose left column (description)
      let descriptionParts: string[] = [];

      if (fields.label) descriptionParts.push(fields.label);
      if (fields.commonDenomination) descriptionParts.push(`(${fields.commonDenomination})`);
      if (fields.concentration) descriptionParts.push(fields.concentration);
      if (fields.presentation) descriptionParts.push(fields.presentation);

      let description = descriptionParts.join(' ').replace(/\s+/g, ' ').trim();

      if (fields.description) {
        description += `\n- ${fields.description}`;
      }

      if (fields.frequencyTime && fields.frequencyUnit) {
        description += ` c/${fields.frequencyTime} ${fields.frequencyUnit}`;
      }

      entries.push({
        label: '',
        value: description,
      });
  
      entries.push({
        label: 'Cantidad',
        value: fields.quantity,
      });
    }
  
    return entries;
  }

  private shapeTreatment(obj: any) {
    const treatment = obj.treatment;
    if (!treatment || typeof treatment !== 'string') return [];
  
    return treatment
      .split('###')
      .map(entry => entry.trim())
      .filter(entry => entry.length > 0)
      .map(entry => {
        const lines = entry
          .split('##')
          .map(line => line.trim())
          .filter(line => line.length > 0);
  
        return { lines };
      });
  }

  private shapeBasicPatientData(obj: any) {
    return {
      fullName: `${obj?.firstName ?? ''} ${obj?.fatherName ?? ''} ${obj?.motherName ?? ''}`.trim() || this.defaultFallback,
      dni: obj?.documentNumber ?? this.defaultFallback,
      birthDate: obj?.birthDate ?? this.defaultFallback,
      age: obj?.birthDate && obj?.dateReport ? this.pdfUtils.formatAge(obj.dateReport, obj.birthDate) : this.defaultFallback,
      sex: obj?.sex ?? this.defaultFallback,
    };
  }

  private shapeDetailedPatientData(patient: any) {
    const today = new Date();
    // const baseFormat: Intl.DateTimeFormatOptions = {
    //   year: 'numeric',
    //   month: '2-digit',
    //   day: '2-digit',
    // };
    return {
      fullName: `${patient?.firstName ?? ''} ${patient?.lastNameFather ?? ''} ${patient?.lastNameMother ?? ''}`.trim() || this.defaultFallback,
      dni: patient?.documentNumber ?? this.defaultFallback,
      birthDate: patient?.birthDate ? this.pdfUtils.formatDate(patient.birthDate, {hideTime: true}) : this.defaultFallback,
      age: patient?.birthDate ? this.pdfUtils.formatAge(today.toISOString(), patient.birthDate) : this.defaultFallback,
      sex: patient?.sex ?? this.defaultFallback,
      occupation: this.isNullOrEmpty(patient?.ocupation) ? this.defaultFallback : patient?.ocupation,
      education: this.isNullOrEmpty(patient?.gradeStudy) ? this.defaultFallback : patient?.gradeStudy,
      maritalStatus: this.isNullOrEmpty(patient?.civilStatus) ? this.defaultFallback : patient?.civilStatus,
      email: this.isNullOrEmpty(patient?.eMail) ? this.defaultFallback : patient?.eMail,
      country: this.isNullOrEmpty(patient?.birthCountryName) ? this.defaultFallback : patient?.birthCountryName,
      address: this.isNullOrEmpty(patient?.address) ? this.defaultFallback : this.shapeAddress(patient),
      phone: this.isNullOrEmpty(patient?.telePhone) ? this.defaultFallback : patient?.telePhone,
      insurance: this.isNullOrEmpty(patient?.insuranceName) ? this.defaultFallback : patient?.insuranceName,
      insuranceNumber: this.isNullOrEmpty(patient?.insuranceNumber) ? this.defaultFallback : patient?.insuranceNumber,
      affiliationDate: this.isNullOrEmpty(patient?.affiliationDate) ? this.defaultFallback : this.pdfUtils.formatDate(patient?.affiliationDate) 
    }
  }

  private shapeNotes(obj: any){
    return {
      specialty: this.isNullOrEmpty(obj?.specialty) ? this.defaultFallback : obj?.specialty.toLowerCase(),
      notes: this.isNullOrEmpty(obj?.story) ? this.defaultFallback : obj?.story
    }
  }

  private shapeAddress(obj: any) {
    const locationHierarchy = [...(obj?.locationHierarchy || [])].sort((a, b) => a.levelOrder - b.levelOrder);
    const lowestLevelLocation = locationHierarchy[0]?.locationName;
    const highestLevelLocation = locationHierarchy[locationHierarchy.length - 1]?.locationName;
    const addressParts = [];

    if (obj?.address) {
      addressParts.push(obj.address.toString());
    }
    if (highestLevelLocation && highestLevelLocation !== obj?.address) {
      addressParts.push(highestLevelLocation);
    }
    if (lowestLevelLocation && lowestLevelLocation !== highestLevelLocation && lowestLevelLocation !== obj?.address) {
      addressParts.push(lowestLevelLocation);
    }
    const fullAddress = addressParts.join(', ');

    return fullAddress;
  }

  private shapeAffiliation(obj: any) {
    return {
      fullName: `${obj?.firstName ?? ''} ${obj?.fatherName ?? ''} ${obj?.motherName ?? ''}`.trim() || this.defaultFallback,
      dni: obj?.documentNumber ?? this.defaultFallback,
      insurance: obj?.insuranceName ?? this.defaultFallback,
      insuranceNumber: obj?.insuranceNumber ?? this.defaultFallback,
      nhc: obj?.nHc ?? this.defaultFallback,
      birthDate: obj?.birthDate ?? this.defaultFallback,
      age: obj?.birthDate && obj?.dateReport ? this.pdfUtils.formatAge(obj.dateReport, obj.birthDate) : this.defaultFallback,
      sex: obj?.sex ?? this.defaultFallback,
      bloodType: obj?.bloodType ?? 'Desconocido',
    };
  }

  private shapeMetadata(obj: any) {
    const dateReport = obj?.dateReport ?? null;

    return {
      dateReport: dateReport ? this.pdfUtils.formatDate(dateReport) : this.defaultFallback,
      consultType: obj?.isHospitalization
        ? 'Hospitalización'
        : obj?.isTeleconsultation
        ? 'Teleconsulta'
        : 'Presencial',
      motive: this.isNullOrEmpty(obj?.motive) ? 'No especificado' : obj?.motive,
      isHospitalization: !!obj?.isHospitalization,
      category: this.isNullOrEmpty(obj?.cateogry) ? 'attention' : obj?.cateogry  // or 'evaluation'
    };
  }

  private shapeFooter(obj: any) {
    return {
      fullName: `${obj?.firstName ?? ''} ${obj?.fatherName ?? ''} ${obj?.motherName ?? ''}`.trim() || this.defaultFallback,
      documentNumber: obj?.documentNumber ?? this.defaultFallback,
    }
  }

  private shapeSignature(obj: any) {
    const dateReport = obj?.dateReport ?? null;
    return {
      signature: obj.signatureDrawing ? ('data:image/png;base64,' + obj.signatureDrawing.toString()) : (obj.signatuteDraw ? ('data:image/png;base64,' + obj.signatuteDraw.toString()) : null),
      signatureText: obj.signatureText ? obj.signatureText : (obj.signatuteText ? obj.signatuteText : ""),
      medicName: obj.medicName ?? "-",
      professionalId: obj?.medicCmp ?? "-",
      date: dateReport ? this.pdfUtils.formatDate(dateReport, {hideTime: true}) : this.defaultFallback,
    }
  }

  private shapeBiologicalFunctions(obj: any) {
    return [
      { label: 'Apetito', value: obj?.hunger.toLowerCase() ?? this.defaultFallback },
      { label: 'Sed', value: obj?.thirst.toLowerCase() ?? this.defaultFallback },
      { label: 'Orina', value: obj?.urine.toLowerCase() ?? this.defaultFallback },
      { label: 'Deposiciones', value: obj?.deposition.toLowerCase() ?? this.defaultFallback },
      { label: 'Sueño', value: obj?.sleep.toLowerCase() ?? this.defaultFallback },
    ];
  }

  private shapeVitalSigns(obj: any, includeGlasgow = true) {
    const vitalSigns = [
      {
        label: 'Presión Arterial',
        value: (obj?.systolicBloodPressure > 0 && obj?.diastolicBloodPressure > 0) ? `${obj?.systolicBloodPressure} / ${obj?.diastolicBloodPressure }` : '-'
      },
      { label: 'Frecuencia Cardíaca', value: obj?.heartRate > 0 ? obj?.heartRate : '-' },
      { label: 'Frecuencia Respiratoria', value: obj?.breathingRate > 0 ? obj?.breathingRate : '-' },
      { label: 'Peso', value: obj?.weight > 0 ? obj?.weight : '-' },
      { label: 'Talla', value: obj?.size > 0 ? obj?.size : '-' },
      { label: 'Sat. O2.', value: obj?.saturation > 0 ? obj?.saturation : '-' },
      { label: 'Temperatura', value: obj?.temperature > 0 ? obj?.temperature : '-' },
      { label: 'IMC', value: obj?.bodyMassIndex > 0 ? obj?.bodyMassIndex : '-' },
    ];

    if (includeGlasgow) {
      vitalSigns.push({ label: 'Puntaje Glasgow', value: obj?.valueGlasgowScale > 0 ? obj?.valueGlasgowScale : '-' })
    }
    return vitalSigns;
  }

  private shapePhysicalExam(obj: any) {
    return [
      { label: 'Piel', value: obj?.skin ?? this.defaultFallback },
      { label: 'TCS', value: obj?.tcs ?? this.defaultFallback },
      { label: 'Cabeza', value: obj?.head ?? this.defaultFallback },
      { label: 'Cuello', value: obj?.neck ?? this.defaultFallback },
      { label: 'Tórax', value: obj?.chest ?? this.defaultFallback },
      { label: 'Pulmones', value: obj?.lungs ?? this.defaultFallback },
      { label: 'Corazón', value: obj?.heart ?? this.defaultFallback },
      { label: 'Mamas y Axilas', value: obj?.breast ?? this.defaultFallback },
      { label: 'Abdomen', value: obj?.abdomen ?? this.defaultFallback },
      { label: 'Apto. Génito-Urinario', value: obj?.urinaryGenital ?? this.defaultFallback },
      { label: 'Examen Rectal y Perianal', value: obj?.rectal ?? this.defaultFallback },
      { label: 'Extremidades', value: obj?.extremities ?? this.defaultFallback },
      { label: 'Vascular Periférico', value: obj?.peripheralVascular ?? this.defaultFallback },
      { label: 'Exámen Neurológico', value: obj?.neurologicalExam ?? this.defaultFallback },
    ];
  }

  private shapeGeneralExam(obj: any) {
    return {
      generalStatus: this.isNullOrEmpty(obj?.statusGeneral) ? this.defaultFallback : obj?.statusGeneral,
      description: this.isNullOrEmpty(obj?.physicalExamDescription) ? 'No especificada' : obj?.physicalExamDescription
    };
  }

  private shapeClinicalExamSection(obj: any) {
    return {
      vitalSigns: this.shapeVitalSigns(obj),
      general: this.shapeGeneralExam(obj),
      physicalExam: this.shapePhysicalExam(obj)
    };
  }
}

function createDebugProxy(shaped: {
  metadata: {
    dateReport: string; consultType: string; motive: any; isHospitalization: boolean; category: any; // or 'evaluation'
  }; affiliation: { fullName: string; dni: any; insurance: any; insuranceNumber: any; nhc: any; birthDate: any; age: string; sex: any; bloodType: any; }; anamnesis: { reason: any; currentIllness: any; personalHistory: any; paternalHistory: any; maternalHistory: any; allergies: any; medications: any; biologicalFunctions: { label: string; value: any; }[]; sicknessTime: any; sicknessUnit: any; symptomsList: any; additionalComments: any; }; clinicalExam: { vitalSigns: { label: string; value: any; }[]; general: { generalStatus: any; description: any; }; physicalExam: { label: string; value: any; }[]; }; diagnosis: any; treatment: { label: string; value: string; }[]; footer: { fullName: string; documentNumber: any; }; signature: { signature: string; signatureText: any; medicName: any; professionalId: any; date: string; };
}, arg1: string) {
  throw new Error('Function not implemented.');
}

